// Navigation guards ("you have unsaved changes") over TanStack Router's blocker.
//
// `useNavigationGuard` blocks in-app navigations (links, router pushes, back/forward) while `enabled`
// says so, and lets `confirm` decide; without `confirm` the hook exposes `active`/`accept`/`reject`
// for a custom dialog. Query-only changes on the same path are never guarded. Leaving the site
// (reload, closing the tab) is covered separately with `beforeunload` by the caller.
import { useBlocker } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";

export interface NavigationGuardParams {
  to: string;
  type: "push" | "replace" | "popstate";
}

export type NavigationGuardCallback = (params: NavigationGuardParams) => boolean | Promise<boolean>;

export interface NavigationGuardOptions {
  /** @default true */
  enabled?: boolean | ((params: NavigationGuardParams) => boolean);
  confirm?: NavigationGuardCallback;
}

const ACTION_TYPES: Record<string, NavigationGuardParams["type"]> = {
  PUSH: "push",
  REPLACE: "replace",
};

export function useNavigationGuard(options: NavigationGuardOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [pending, setPending] = useState<{ resolve: (accepted: boolean) => void } | null>(null);

  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: async ({ current, next, action }) => {
      if (current.pathname === next.pathname) return false;
      const params: NavigationGuardParams = {
        to: next.pathname,
        type: ACTION_TYPES[action] ?? "popstate",
      };
      const { enabled = true, confirm } = optionsRef.current;
      if (!(typeof enabled === "function" ? enabled(params) : enabled)) return false;
      const accepted = confirm
        ? await confirm(params)
        : await new Promise<boolean>((resolve) => setPending({ resolve }));
      return !accepted;
    },
  });

  const accept = useCallback(() => {
    pending?.resolve(true);
    setPending(null);
  }, [pending]);

  const reject = useCallback(() => {
    pending?.resolve(false);
    setPending(null);
  }, [pending]);

  return { active: pending !== null, accept, reject };
}
